import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChevronRightIcon from '@mui/icons-material/ChevronRightOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReportOutlineIcon from '@mui/icons-material/ReportOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { startCase } from 'lodash-es';
import { useState } from 'react';

import { PseudoLink } from '~components/Helpers/PseudoLink';
import { TicketMetadata } from '~hooks/useTickets/models';

import ReportScanIssueDialog from './ReportScanIssueDialog';

interface TicketMetadataSummaryProps {
  metadata: TicketMetadata;
}

type StructuredDataValue =
  | string
  | number
  | boolean
  | null
  | StructuredDataObject
  | StructuredDataValue[];

interface StructuredDataObject {
  [key: string]: StructuredDataValue;
}

interface StructuredDataProps {
  data: StructuredDataObject;
  level?: number;
}

interface StructuredDataItemProps {
  itemKey: string;
  value: StructuredDataValue;
  level?: number;
}

const isStructuredDataObject = (
  value: StructuredDataValue,
): value is StructuredDataObject =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const isStructuredDataArray = (
  value: StructuredDataValue,
): value is StructuredDataValue[] => Array.isArray(value);

function StructuredDataItem({ itemKey, value, level = 0 }: StructuredDataItemProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(level < 2);
  const isObject = isStructuredDataObject(value);
  const isArray = isStructuredDataArray(value);
  const isExpandable = isObject || isArray;

  return (
    <Box sx={{ position: 'relative', px: 0.5 }}>
      <Box
        sx={{
          position: 'relative',
          // add a vertical line as a guide for the nested items
          '&::before':
            level > 0
              ? {
                  content: '""',
                  position: 'absolute',
                  left: '5px',
                  top: 0,
                  bottom: 0,
                  width: '1px',
                  backgroundColor: theme.brandV2.colors.treadGray7,
                }
              : {},
          pl: level > 0 ? 2 : 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 24 }}>
          {isExpandable && (
            <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ p: 0 }}>
              {expanded ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )}
            </IconButton>
          )}

          <>
            <Typography
              variant="body2"
              component="span"
              sx={{ fontWeight: 500, ml: 0.5, mr: 1.5, textWrap: 'nowrap' }}
            >
              {startCase(itemKey)}
            </Typography>

            {!isExpandable && (
              <Typography
                variant="body2"
                sx={{
                  color: `${theme.brandV2.colors.treadGray2}`,
                  wordBreak: 'break-word',
                }}
              >
                {String(value)}
              </Typography>
            )}
          </>
        </Box>

        {isExpandable && (
          <Collapse in={expanded}>
            <Box>
              {isArray
                ? value.map((item, index: number) => (
                    <StructuredDataItem
                      key={index}
                      itemKey={`[${index}]`}
                      value={item}
                      level={level + 1}
                    />
                  ))
                : isObject &&
                  Object.entries(value).map(([key, val]) => (
                    <StructuredDataItem
                      key={key}
                      itemKey={key}
                      value={val}
                      level={level + 1}
                    />
                  ))}
            </Box>
          </Collapse>
        )}
      </Box>
    </Box>
  );
}

function StructuredData({ data, level = 0 }: StructuredDataProps) {
  const theme = useTheme();
  const entries = Object.entries(data);
  const [showStructuredData, setShowStructuredData] = useState(false);

  return (
    <>
      <PseudoLink
        title={showStructuredData ? t('actions.hide_details') : t('actions.show_details')}
        action={() => setShowStructuredData(!showStructuredData)}
        sx={{ my: 1 }}
      />
      <Collapse in={showStructuredData}>
        <Box
          sx={{
            backgroundColor: `${theme.brandV2.colors.treadGray8}`,
            border: `1px solid ${theme.brandV2.colors.treadGray7}`,
            p: 1,
            py: 2,
            borderRadius: 1,
            mt: 2,
          }}
        >
          {entries.map(([key, value]) => (
            <StructuredDataItem key={key} itemKey={key} value={value} level={level} />
          ))}
        </Box>
      </Collapse>
    </>
  );
}

export default function TicketMetadataSummary({ metadata }: TicketMetadataSummaryProps) {
  const theme = useTheme();
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);

  // remove "full_description" key from metadata since we display this separately
  const cleanedStructuredData = Object.fromEntries(
    Object.entries(metadata.aiStructuredData ?? {}).filter(
      ([key]) => key !== 'full_description',
    ),
  );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          my: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 16 }} />
            <Typography variant="subtitle2">
              {t('ticket_drawer_v2.details.ai_summary')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <ReportOutlineIcon
              sx={{
                fontSize: 16,
                color: theme.brandV2.colors.treadGray3,
                mt: 0.25,
              }}
            />
            <PseudoLink
              title={t('ticket_drawer_v2.details.report_scanning_issue')}
              action={() => setIsReportDialogOpen(true)}
              linkColor={theme.brandV2.colors.treadGray3}
            />
          </Box>
        </Box>
        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            backgroundColor: `${theme.brandV2.colors.treadGray9}`,
            border: `1px solid ${theme.brandV2.colors.treadGray8}`,
          }}
        >
          <Typography variant="body2">{metadata.aiExtractedFullText}</Typography>
          {Object.keys(cleanedStructuredData).length > 0 && (
            <StructuredData data={cleanedStructuredData} />
          )}
        </Box>
      </Box>

      <ReportScanIssueDialog
        isOpen={isReportDialogOpen}
        onClose={() => setIsReportDialogOpen(false)}
        ticketId={metadata.id}
      />
    </Box>
  );
}
